import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export function useGradientModel(params) {
  const { defaultValue, onChangeStart, onChange, onChangeEnd } = params;

  const [model, setModel] = useState(defaultValue);
  const modelRef = useRef(model);
  modelRef.current = model;

  const onChangeStartRef = useRef(onChangeStart);
  onChangeStartRef.current = onChangeStart;

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const onChangeEndRef = useRef(onChangeEnd);
  onChangeEndRef.current = onChangeEnd;

  const rafIdRef = useRef(0);
  const pendingChangeRef = useRef(null);

  const flushPending = useCallback(() => {
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = 0;
    }

    if (pendingChangeRef.current) {
      onChangeRef.current?.(pendingChangeRef.current);
      pendingChangeRef.current = null;
    }
  }, []);

  useEffect(
    () => () => {
      flushPending();
    },
    [flushPending]
  );

  const activeColorStop = useMemo(
    () => model.colorStops.find((s) => s.id === model.activeColorStopId),
    [model.activeColorStopId, model.colorStops]
  );

  const handleModelChange = useCallback(
    ({ patch, reason, phase }) => {
      const prev = modelRef.current;
      const resolvedPatch = typeof patch === 'function' ? patch(prev) : patch;
      const next = { ...prev, ...resolvedPatch };

      modelRef.current = next;
      setModel(next);

      const change = { model: next, reason };

      if (phase === 'change') {
        if (!rafIdRef.current) {
          onChangeRef.current?.(change);

          rafIdRef.current = requestAnimationFrame(() => {
            rafIdRef.current = 0;

            if (pendingChangeRef.current) {
              onChangeRef.current?.(pendingChangeRef.current);
              pendingChangeRef.current = null;
            }
          });
        } else {
          pendingChangeRef.current = change;
        }
      } else {
        // Start and end fire immediately.
        // Flush any queued onChange first so change callbacks stay in the expected order.
        flushPending();

        if (phase === 'start') {
          onChangeStartRef.current?.(change);
        } else {
          onChangeEndRef.current?.(change);
        }
      }
    },
    [flushPending]
  );

  return { model, activeColorStop, handleModelChange };
}
